import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import debounce from 'lodash/debounce'

import { IActionRepeaterValue, TypographyGroupValue, TypographySelectValue } from '@makyoapp/makyo-common'
import { useRtl } from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db'
import { CoreComponentAction } from '@/core/models/app-components/types'
import { IRow } from '@/core/models/tables/row'
import { useCompContext } from '@/context/__project__/CompContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { ComponentSettingKey } from '@/shared/builder'
import { AspectRatio, CardVariant, MdShape } from '@/shared/builder/types'
import { getSafely } from '@/shared/safely'

import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'
import { getColBrief } from '../../utils'
import { BaseCompCardItemProps } from './item.comp'

function transposeChunk(chunk: IRow[], column: number, row: number): IRow[] {
  const result: IRow[] = []
  for (let r = 0; r < row; r++) {
    for (let c = 0; c < column; c++) {
      const idx = r * column + c
      if (idx < chunk.length) {
        result.push(chunk[idx])
      }
    }
  }
  return result
}

export interface BaseCompCardProps {
  settings: Record<string, any>
  table: IDBTable
  rowIndexesMap: Map<string, number>
  items: IRow[]
}

export interface CompCardProps extends BaseCompCardProps {
  action: CoreComponentAction
  children: (props: BaseCompCardItemProps) => React.ReactNode
}

/**
 * CompCardCore
 */
export const CompCardCore = React.memo(({ settings, table, rowIndexesMap, items, children }: CompCardProps) => {
  const dir = useRtl()
  const isRTL = dir === 'rtl'

  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { component } = useCompContext()

  const handleCardClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= data =========================
  const headerCol = getColBrief(table, settings[ComponentSettingKey.CardDataHeader] as string)
  const subheaderCol = getColBrief(table, settings[ComponentSettingKey.CardDataSubHeader] as string)
  const thumbCol = getColBrief(table, settings[ComponentSettingKey.CardDataThumbnail] as string)
  const mediaCol = getColBrief(table, settings[ComponentSettingKey.CardDataMedia] as string)
  const titleCol = getColBrief(table, settings[ComponentSettingKey.CardDataTitle] as string)
  const subtitleCol = getColBrief(table, settings[ComponentSettingKey.CardDataSubtitle] as string)
  const supportingTextCol = getColBrief(table, settings[ComponentSettingKey.CardDataSupportingText] as string)

  const footerLeadingActionEnabled = Boolean(settings[ComponentSettingKey.CardDataLeadingAction])
  const footerLeadingActionIcon = settings[ComponentSettingKey.CardDataLeadingActionIcon] as string
  const footerLeadingActionIconEnd = settings[ComponentSettingKey.CardDataLeadingActionIconPosition] === 'trailing'
  const footerLeadingActions = settings[ComponentSettingKey.CardActionLeadingClick] as IActionRepeaterValue[]

  const footerLeading = useMemo(() => {
    return footerLeadingActionEnabled
      ? {
          icon: footerLeadingActionIcon,
          actions: footerLeadingActions,
          end: footerLeadingActionIconEnd,
        }
      : undefined
  }, [footerLeadingActionEnabled, footerLeadingActionIcon, footerLeadingActionIconEnd, footerLeadingActions])

  const footerTrailingActionEnabled = Boolean(settings[ComponentSettingKey.CardDataTrailingAction])
  const footerTrailingActionIcon = settings[ComponentSettingKey.CardDataTrailingActionIcon] as string
  const footerTrailingActionIconEnd = settings[ComponentSettingKey.CardDataTrailingActionIconPosition] === 'trailing'
  const footerTrailingActions = settings[ComponentSettingKey.CardActionTrailingClick] as IActionRepeaterValue[]

  const footerTrailing = useMemo(() => {
    return footerTrailingActionEnabled
      ? {
          icon: footerTrailingActionIcon,
          actions: footerTrailingActions,
          end: footerTrailingActionIconEnd,
        }
      : undefined
  }, [footerTrailingActionEnabled, footerTrailingActionIcon, footerTrailingActionIconEnd, footerTrailingActions])

  // ========================= styles =========================

  const typoSettings = settings[ComponentSettingKey.CardDesignTypography] as TypographyGroupValue
  const metadataTypo = typoSettings?.[ComponentSettingKey.CardDesignMetadataTypography]
  const actionHelpTypo = typoSettings?.[ComponentSettingKey.CardDesignActionHelpTypography]
  const headerTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.CardDesignHeaderTypography],
    {} as TypographySelectValue
  )
  const subheaderTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.CardDesignSubHeaderTypography],
    {} as TypographySelectValue
  )
  const titleTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.CardDesignTitleTypography],
    {} as TypographySelectValue
  )
  const subtitleTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.CardDesignSubtitleTypography],
    {} as TypographySelectValue
  )
  const supportingTextTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.CardDesignSupportingTextTypography],
    {} as TypographySelectValue
  )

  const textLine1Limit = settings[ComponentSettingKey.CardDesignHeaderLines]?.[0] ?? 1
  const textLine2Limit = settings[ComponentSettingKey.CardDesignSubHeaderLines]?.[0] ?? 1
  const textLine3Limit = settings[ComponentSettingKey.CardDesignTitleLines]?.[0] ?? 1
  const textLine4Limit = settings[ComponentSettingKey.CardDesignSubtitleLines]?.[0] ?? 1
  const textLine5Limit = settings[ComponentSettingKey.CardDesignSupportingTextLines]?.[0] ?? 1
  const textLine6Limit = settings[ComponentSettingKey.CardDesignMetadataLines]?.[0] ?? 1

  const column = (settings[ComponentSettingKey.CardDesignNumCols] as number) ?? 1
  const row = (settings[ComponentSettingKey.CardDesignNumRows] as number) ?? 1
  const orientation = settings[ComponentSettingKey.CardDesignOrientation] || 'vertical'

  const equalHeight = Boolean(settings[ComponentSettingKey.CardDesignSameHeight])
  const itemShape = settings[ComponentSettingKey.CardDesignShape] as MdShape
  const itemVariant = settings[ComponentSettingKey.CardDesignVariant] as CardVariant
  const mediaCorner = settings[ComponentSettingKey.CardDesignMediaShape] as MdShape
  const mediaRatio = settings[ComponentSettingKey.CardDesignMediaAspectRatio] as AspectRatio
  const noBorder = Boolean(settings[ComponentSettingKey.CardDesignOutlinedNoBorder])
  const noPadding = Boolean(settings[ComponentSettingKey.CardDesignOutlinedNoPadding])

  // const colAsRow = Boolean((settings[ComponentSettingKey.CardDesignColumnRow] as string) === 'row')

  const itemGap = settings[ComponentSettingKey.CardDesignItemGap]
  const itemPadding = settings[ComponentSettingKey.CardDesignItemPadding]

  // ========================= state =========================
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const [stack, setStack] = useState(false)
  // const [forceSameHeight, setForceSameHeight] = useState(equalHeight)

  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const containerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const isUpdatingRef = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Enhanced calculateMaxHeight dengan force reset option
  const calculateMaxHeight = useCallback((forceReset = false, debugInfo = '') => {
    if (isUpdatingRef.current && !forceReset) return

    isUpdatingRef.current = true

    const measure = () => {
      const heights = Array.from(cardRefs.current.values()).map((el) => {
        if (forceReset) {
          el.style.height = 'auto'
          el.offsetHeight // Force reflow
        }

        const rect = el.getBoundingClientRect()
        const height = rect.height

        return height
      })

      if (heights.length > 0) {
        const validHeights = heights.filter((h) => h > 0)
        if (validHeights.length > 0) {
          const newMaxHeight = Math.max(...validHeights)
          setMaxHeight(newMaxHeight)
        }
      }
      isUpdatingRef.current = false
    }

    if (forceReset) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          measure()
        })
      })
    } else {
      measure()
    }
  }, [])

  // Debounced version for performance
  const debouncedCalculateMaxHeight = useMemo(
    () => debounce((forceReset = false, debugInfo = '') => calculateMaxHeight(forceReset, debugInfo), 100),
    [calculateMaxHeight]
  )

  // Enhanced registerCardRef
  const registerCardRef = useCallback(
    (id: string, element: HTMLDivElement) => {
      cardRefs.current.set(id, element)

      if (equalHeight) {
        // Observe individual card with ResizeObserver
        if (resizeObserverRef.current) {
          resizeObserverRef.current.observe(element)
        }

        // Delayed calculation
        timeoutRef.current = setTimeout(() => {
          debouncedCalculateMaxHeight(false, `registerCardRef-${id}`)
        }, 50)
      }
    },
    [equalHeight, debouncedCalculateMaxHeight]
  )

  // Comprehensive dependencies tracking
  const heightAffectingDeps = useMemo(
    () => [
      // Column data dependencies
      mediaCol?._Settings?._id,
      headerCol?._Settings?._id,
      subheaderCol?._Settings?._id,
      subtitleCol?._Settings?._id,
      supportingTextCol?._Settings?._id,
      thumbCol?._Settings?._id,
      titleCol?._Settings?._id,

      // Line limits that affect height
      textLine1Limit,
      textLine2Limit,
      textLine3Limit,
      textLine4Limit,
      textLine5Limit,
      textLine6Limit,

      // Layout affecting props
      itemVariant,
      itemShape,
      mediaCorner,
      mediaRatio,
      noBorder,
      noPadding,
      itemPadding?.value,
      itemGap?.value,
      orientation,

      // Items data (for content changes)
      items.length,
    ],
    [
      mediaCol,
      headerCol,
      subheaderCol,
      subtitleCol,
      supportingTextCol,
      thumbCol,
      titleCol,
      textLine1Limit,
      textLine2Limit,
      textLine3Limit,
      textLine4Limit,
      textLine5Limit,
      textLine6Limit,
      itemVariant,
      itemShape,
      mediaCorner,
      mediaRatio,
      noBorder,
      noPadding,
      itemPadding,
      itemGap,
      orientation,
      items,
    ]
  )

  // TODO: these effects might affect the performance, we should find a way to optimize them

  useLayoutEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined

    if (equalHeight) {
      if (column > 1) {
        setMaxHeight(0)
        cardRefs.current.clear()
      }

      timeoutId = setTimeout(() => {
        debouncedCalculateMaxHeight(true, 'heightAffectingDeps-useLayoutEffect')
      }, 100)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [heightAffectingDeps, equalHeight, debouncedCalculateMaxHeight, column])

  // Enhanced MutationObserver for backup detection
  useEffect(() => {
    if (equalHeight) {
      // Create new ResizeObserver
      resizeObserverRef.current = new ResizeObserver(() => {
        debouncedCalculateMaxHeight()
      })

      observerRef.current = new MutationObserver((mutations) => {
        const shouldRecalculate = mutations.some((mutation) => {
          const target = mutation.target as HTMLElement
          if (!target || !('closest' in target)) return false
          return (
            target?.closest('[data-card-container]') !== null ||
            (mutation.type === 'attributes' && ['style', 'class', 'src'].includes(mutation.attributeName || '')) ||
            mutation.type === 'childList' ||
            mutation.type === 'characterData'
          )
        })

        if (shouldRecalculate) {
          debouncedCalculateMaxHeight()
        }
      })

      if (containerRef.current) {
        observerRef.current.observe(containerRef.current, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeOldValue: true,
          characterData: true,
          characterDataOldValue: true,
        })
      }
    }
  }, [equalHeight, debouncedCalculateMaxHeight])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined

    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = undefined
    }

    // Layout changes effect (column, row)
    if (equalHeight) {
      setMaxHeight(0)
      timeoutId = setTimeout(() => {
        debouncedCalculateMaxHeight(true)
      }, 100)
    }
    // Reset maxHeight when equalHeight disabled
    else {
      setMaxHeight(0)
      cardRefs.current.clear()
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [column, row, equalHeight, debouncedCalculateMaxHeight])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
      debouncedCalculateMaxHeight.cancel()
    }
  }, [debouncedCalculateMaxHeight])

  // ========================= render =========================

  const renderCardItems = useCallback(
    (data: IRow, rowIndex: number, equalHeight: boolean) => {
      return children({
        tableId: table.settings._id,
        data: data,
        rowIndex,
        settings,
        footerLeading,
        footerTrailing,
        shape: itemShape,
        headerCol,
        thumbCol,
        subheaderCol,
        mediaCol,
        titleCol,
        subtitleCol,
        supportingTextCol,
        mediaCorner,
        mediaRatio,
        numRow: row,
        orientation,
        metadataTypo,
        actionHelpTypo,
        headerTypo,
        subheaderTypo,
        titleTypo,
        subtitleTypo,
        supportingTextTypo,
        textLine1Limit,
        textLine2Limit,
        textLine3Limit,
        textLine4Limit,
        textLine5Limit,
        textLine6Limit,
        maxHeightPerItem: equalHeight ? maxHeight : undefined,
        stacked: stack,
        onMaxHeightChange: (height: number) => {
          if (maxHeight < height) {
            setMaxHeight(height)
          }
        },
        onStackedChange: (stacked: boolean) => {
          setStack(stacked)
        },
        itemPadding: itemPadding,
        itemGap: itemGap,
        equalHeight,
      })
    },
    [
      actionHelpTypo,
      children,
      row,
      footerLeading,
      footerTrailing,
      headerCol,
      itemShape,
      mediaCol,
      mediaCorner,
      mediaRatio,
      metadataTypo,
      orientation,
      settings,
      subheaderCol,
      subtitleCol,
      supportingTextCol,
      table.settings._id,
      thumbCol,
      titleCol,
      headerTypo,
      subheaderTypo,
      titleTypo,
      subtitleTypo,
      supportingTextTypo,
      textLine1Limit,
      textLine2Limit,
      textLine3Limit,
      textLine4Limit,
      textLine5Limit,
      textLine6Limit,
      itemGap,
      itemPadding,
      maxHeight,
      stack,
    ]
  )

  const renderCard = useCallback(
    (items: IRow[]) => {
      if (items.length === 0) return null

      const isHorizontal = orientation === 'horizontal'
      const isVertical = orientation !== 'horizontal'

      if (isHorizontal) {
        const sizeToSplit = row * column
        const chunks: IRow[][] = []

        for (let i = 0; i < items.length; i += sizeToSplit) {
          chunks.push(items.slice(i, i + sizeToSplit))
        }

        return (
          <div
            className='flex flex-row overflow-x-auto no-scrollbar max-w-full w-full'
            style={{
              gap: `${itemGap?.value ?? 16}px`,
            }}
          >
            {chunks.map((chunk, chunkIndex) => {
              const transposed = transposeChunk(chunk, column, row)
              const actualItemsInChunk = chunk.length
              const isLastChunk = chunkIndex === chunks.length - 1
              const isPartialChunk = actualItemsInChunk < row * column

              const actualRowsNeeded = isLastChunk && isPartialChunk ? Math.ceil(actualItemsInChunk / column) : row
              const actualColumnsNeeded = isLastChunk && isPartialChunk ? Math.min(column, actualItemsInChunk) : column

              return (
                <div
                  key={`chunk-${chunkIndex}-${equalHeight}-${maxHeight}-${stack}`}
                  className='shrink-0'
                  style={{
                    width: '90%',
                    minWidth: '90%',
                    maxWidth: '90%',
                  }}
                >
                  <div
                    className='grid'
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${actualColumnsNeeded}, minmax(0, 1fr))`,
                      gridTemplateRows:
                        equalHeight && maxHeight > 0
                          ? `repeat(${actualRowsNeeded}, ${maxHeight}px)`
                          : `repeat(${actualRowsNeeded}, auto)`,
                      width: '100%',
                      gap: itemGap?.value ? `${itemGap.value}px` : '16px',
                    }}
                  >
                    {transposed.map((item) => {
                      const itemIndex = rowIndexesMap.get(item._id) ?? -1
                      return (
                        <div
                          data-card-container
                          key={item._id}
                          ref={(el) => {
                            if (el) registerCardRef(item._id, el)
                          }}
                          className={clsx('flex-1', { 'h-full': equalHeight })}
                          style={{
                            overflow: 'hidden',
                            maxWidth: '100%',
                          }}
                        >
                          {renderCardItems(item, itemIndex, equalHeight)}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )
      }

      if (column > 1) {
        // More efficient grouping of items into rows
        const newItems: IRow[][] = []
        for (let i = 0; i < items.length; i += column) {
          newItems.push(items.slice(i, i + column))
        }

        // Common values
        const isMultiple = newItems.length > 1

        return newItems.map((rowData, groupIndex) => {
          return (
            <div
              className={clsx('relative z-1', {
                ['w-full']: isVertical || !isMultiple,
              })}
              key={groupIndex}
            >
              <div
                className={clsx(isVertical ? 'grid' : 'flex', 'relative z-1', isHorizontal && 'flex-row')}
                style={{
                  gridTemplateColumns: isVertical ? `repeat(${column}, minmax(0, 1fr))` : undefined,
                  height: equalHeight ? '100%' : 'fit-content',
                  gap: itemGap?.value ? `${itemGap.value}px` : '16px',
                  ...(equalHeight &&
                    maxHeight > 0 &&
                    isVertical && {
                      gridTemplateRows: `${maxHeight}px`,
                    }),
                }}
                dir={dir}
              >
                {rowData.map((row) => {
                  const rowIndex = rowIndexesMap.get(row._id) ?? -1

                  const horizontalCardStyle =
                    isHorizontal && column > 1
                      ? {
                          boxSizing: 'border-box' as const,
                          flexBasis: `calc((99.8% - ${(column - 1) * 16}px) / ${column})`,
                          maxWidth: `calc((99.8% - ${(column - 1) * 16}px) / ${column})`,
                        }
                      : {}

                  return (
                    <div
                      key={row._id}
                      ref={(el) => el && registerCardRef(row._id, el)}
                      className={clsx('flex-1', isHorizontal && equalHeight && 'h-full')}
                      style={horizontalCardStyle}
                    >
                      {renderCardItems(row, rowIndex, equalHeight)}
                    </div>
                  )
                })}
              </div>

              {/*// TODO: use Separator instead */}
              {/* {!isLast && isVertical && divider && <MdDivider className='pt-4' />} */}
            </div>
          )
        })
      } else {
        // Single column layout
        return items.map((dataRow, index) => {
          const rowIndex = rowIndexesMap.get(dataRow._id) ?? -1
          const isMultiple = items.length > 1

          return (
            <div
              key={dataRow._id}
              ref={(el) => el && registerCardRef(dataRow._id, el)}
              className={clsx('shrink-0', {
                ['w-[90%]']: orientation === 'horizontal' && isMultiple,
                ['w-full']: orientation === 'vertical' || !isMultiple,
              })}
              style={{
                ...(equalHeight &&
                  maxHeight > 0 && {
                    height: `${maxHeight}px`,
                  }),
              }}
            >
              {renderCardItems(dataRow, rowIndex, equalHeight)}

              {/*// TODO: use Separator instead */}
              {/* {!isLast && orientation !== 'horizontal' && divider && <MdDivider className='pt-4' />} */}
            </div>
          )
        })
      }
    },
    [
      column,
      orientation,
      row,
      itemGap,
      maxHeight,
      stack,
      rowIndexesMap,
      equalHeight,
      renderCardItems,
      dir,
      registerCardRef,
    ]
  )

  const render = (
    <div
      ref={containerRef}
      style={{
        ...(isRTL && orientation === 'horizontal' && { transform: 'translateX(0)' }),
        paddingBottom: itemGap?.value ? `${itemGap.value}px` : '16px',
        gap: itemGap?.value ? `${itemGap.value}px` : '16px',
      }}
      className={clsx(
        'box-border flex z-1',
        orientation === 'vertical' && 'flex-col',
        orientation === 'horizontal' && 'overflow-x-auto no-scrollbar full-bleed md3-card-horizontal-full-bleed',
        orientation === 'horizontal' && itemVariant === CardVariant.Outlined && 'px-[0px]',
        orientation === 'horizontal' && itemVariant === CardVariant.Elevated && 'px-[2px]'
      )}
    >
      {renderCard(items)}
    </div>
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {render}
        <CompOverlayDevmode handleClick={handleCardClick} />
      </div>
    )
  }

  return render
})
CompCardCore.displayName = 'CompCardCore'
